import React, { useCallback, useState } from 'react';

import { ArrowDown, ArrowUp } from '@travel/icons/ui';
import type { useNumberIncrementHandler } from '@travel/traveler-core/components/AmountInput/hooks';
import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import { cx } from '@travel/utils';

import labels from 'favoriteList/components/SearchForm/constants/labels';
import CounterForm from './CounterForm';

import styles from './amountInput.module.scss';

type PersonInputProps = {
  className?: string;
  adults: ReturnType<typeof useNumberIncrementHandler>;
  childrenUpperGrade: ReturnType<typeof useNumberIncrementHandler>;
  childrenLowerGrade: ReturnType<typeof useNumberIncrementHandler>;
  infantsBedMeal: ReturnType<typeof useNumberIncrementHandler>;
  infantsBed: ReturnType<typeof useNumberIncrementHandler>;
  infantsMeal: ReturnType<typeof useNumberIncrementHandler>;
  infantsNone: ReturnType<typeof useNumberIncrementHandler>;
  incrementIcon: React.ReactElement;
  decrementIcon: React.ReactElement;
  isDomestic: boolean;
};

export function PersonInput(props: PersonInputProps) {
  const {
    adults,
    childrenUpperGrade,
    childrenLowerGrade,
    infantsBedMeal,
    infantsBed,
    infantsMeal,
    infantsNone,
    incrementIcon,
    decrementIcon,
    isDomestic,
  } = props;
  const [showChildrenInputs, setShowChildrenInputs] = useState(false);
  const isSP = useDeviceType() === MOBILE_SCREEN;

  const numberOfChildren =
    childrenUpperGrade.counter +
    childrenLowerGrade.counter +
    infantsBedMeal.counter +
    infantsBed.counter +
    infantsMeal.counter +
    infantsNone.counter;

  const toggleChildrenIncrementors = useCallback(() => {
    setShowChildrenInputs(!showChildrenInputs);
  }, [showChildrenInputs, setShowChildrenInputs]);

  return (
    <>
      <div className={styles.sectionTitle}>
        <span>{labels.numberOfPeople}</span>
        {!isSP && <span className={styles.normalWeight}> {labels.perRoom}</span>}
      </div>
      <div className={styles.amountWrapper}>
        <p className={cx(styles.textWrapper, styles.amountTitle)}>
          <span data-testid="adult">{labels.adult}</span>
        </p>
        <CounterForm
          {...adults}
          counterType="adult"
          decrementIcon={decrementIcon}
          incrementIcon={incrementIcon}
          isCounterBolded
          counterText={isSP ? labels.peopleCounterPC : undefined}
        />
      </div>
      <div className={cx(styles.amountWrapper, styles.amountTitle, styles.verticalMargin)}>
        <span data-testid="child">{labels.children}</span>
        <div className={styles.childTitleRight}>
          {!isSP && <div className={styles.childCount}>{numberOfChildren}</div>}
          <button
            data-testid="child-arrow"
            type="button"
            className={styles.childArrow}
            onClick={toggleChildrenIncrementors}
          >
            {showChildrenInputs ? (
              <ArrowUp className={styles.childArrowIcon} />
            ) : (
              <ArrowDown className={styles.childArrowIcon} />
            )}
          </button>
        </div>
      </div>
      {showChildrenInputs && (
        <>
          <div className={cx(styles.amountWrapper, styles.shiftLeft)}>
            <p className={styles.textWrapper}>
              <span data-testid="childUpperGrade">
                {isDomestic ? labels.childrenUpperGrade : labels.childrenBed}
              </span>
            </p>
            <CounterForm
              {...childrenUpperGrade}
              counterType="childrenUpperGrade"
              decrementIcon={decrementIcon}
              incrementIcon={incrementIcon}
              isCounterBolded
              counterText={isSP ? labels.peopleCounterPC : undefined}
            />
          </div>
          {isDomestic && (
            <>
              <div className={cx(styles.amountWrapper, styles.shiftLeft)}>
                <p className={styles.textWrapper}>
                  <span data-testid="childLowerGrade">{labels.childrenLowerGrade}</span>
                </p>
                <CounterForm
                  {...childrenLowerGrade}
                  counterType="childrenLowerGrade"
                  decrementIcon={decrementIcon}
                  incrementIcon={incrementIcon}
                  isCounterBolded
                  counterText={isSP ? labels.peopleCounterPC : undefined}
                />
              </div>
              <div className={cx(styles.amountWrapper, styles.shiftLeft)}>
                <p className={styles.textWrapper}>
                  <span data-testid="infantFullSet">{labels.infantsMealAndBed}</span>
                </p>
                <CounterForm
                  {...infantsBedMeal}
                  counterType="infantFullSet"
                  decrementIcon={decrementIcon}
                  incrementIcon={incrementIcon}
                  isCounterBolded
                  counterText={isSP ? labels.peopleCounterPC : undefined}
                />
              </div>
              <div className={cx(styles.amountWrapper, styles.shiftLeft)}>
                <p className={styles.textWrapper}>
                  <span data-testid="infantMealOnly">{labels.infantsMealOnly}</span>
                </p>
                <CounterForm
                  {...infantsMeal}
                  counterType="infantMealOnly"
                  decrementIcon={decrementIcon}
                  incrementIcon={incrementIcon}
                  isCounterBolded
                  counterText={isSP ? labels.peopleCounterPC : undefined}
                />
              </div>
              <div className={cx(styles.amountWrapper, styles.shiftLeft)}>
                <p className={styles.textWrapper}>
                  <span data-testid="infantBedOnly">{labels.infantsBedOnly}</span>
                </p>
                <CounterForm
                  {...infantsBed}
                  counterType="infantBedOnly"
                  decrementIcon={decrementIcon}
                  incrementIcon={incrementIcon}
                  isCounterBolded
                  counterText={isSP ? labels.peopleCounterPC : undefined}
                />
              </div>
            </>
          )}
          <div className={cx(styles.amountWrapper, styles.shiftLeft, styles.resetMarginBottom)}>
            <p className={styles.textWrapper}>
              <span data-testid="infantOnly">
                {isDomestic ? labels.infantsOnly : labels.childrenNoBed}
              </span>
            </p>
            <CounterForm
              {...infantsNone}
              counterType="infant"
              decrementIcon={decrementIcon}
              incrementIcon={incrementIcon}
              isCounterBolded
              counterText={isSP ? labels.peopleCounterPC : undefined}
            />
          </div>
        </>
      )}
    </>
  );
}

export default PersonInput;
